import { sortBy } from "lodash";
import { useCallback } from "react";
import { useStore } from "zustand";

import {
    FilterToCategoriesMapping,
    VerticalToSubVerticalMap,
} from "../../config/categories.config";
import { defaultContentSubTypeForMarketingContent } from "../../config/filters.config";
import { discoveryFilterStore } from "../../store/filters";
import { salesforceStore } from "../../store/salesforce";
import type { Item } from "../../types/common";
import { MarketingNarrativesFilterField } from "../../types/filters/marketingNarratives";
import { getItemsFromCategories } from "../../utils/categories.utils";

export const useMarketingNarrativeFilters = (): {
    applyMarketingNarrativesCascading: () => void;
    applyDefaultMarketingNarrativeFilters: () => void;
} => {
    const [setField, setFilterOptions, hasAppliedFilters] = useStore(
        discoveryFilterStore,
        (state) => [
            state.setField,
            state.setFilterOptions,
            state.hasAppliedFilters,
        ],
    );

    const applyDefaultMarketingNarrativeFilters = (): void => {
        const sfContext = salesforceStore.getState().sfContext;
        const marketingNarrativeFilters = discoveryFilterStore
            .getState()
            .getMarketingContentFilters().MARKETING_NARRATIVES;

        if (
            discoveryFilterStore.getState().getContentSubType().scope ===
                defaultContentSubTypeForMarketingContent.scope &&
            !hasAppliedFilters(
                discoveryFilterStore.getState().getContentSubType(),
            )
        ) {
            // VERTICAL
            const defaultVerticalItem =
                marketingNarrativeFilters.filterOptions.VERTICAL.find(
                    (v) =>
                        v.label.toLowerCase() ===
                        sfContext.vertical?.toLowerCase(),
                );
            if (defaultVerticalItem) {
                setField(
                    MarketingNarrativesFilterField.VERTICAL,
                    defaultVerticalItem,
                    true,
                );
            }

            // MARKETPLACE
            const defaultMarketplaceItem =
                marketingNarrativeFilters.filterOptions.MARKETPLACE.find(
                    (v) =>
                        v.label.toLowerCase() ===
                        sfContext.locale?.toLowerCase(),
                );
            if (defaultMarketplaceItem) {
                setField(
                    MarketingNarrativesFilterField.MARKETPLACE,
                    defaultMarketplaceItem,
                    true,
                );
            }
        }
    };

    // handle cascading between vertical and sub vertical filters
    const applyMarketingNarrativesCascading = useCallback(() => {
        const fieldSelections = discoveryFilterStore
            .getState()
            .getMarketingContentFilters().MARKETING_NARRATIVES.fieldSelections;
        if (fieldSelections.VERTICAL) {
            let items: Item[] = [];
            const filterKey = MarketingNarrativesFilterField.SUBVERTICAL;
            if (fieldSelections.VERTICAL.length === 0) {
                items = getItemsFromCategories(
                    FilterToCategoriesMapping[
                        MarketingNarrativesFilterField.SUBVERTICAL
                    ],
                );
            } else {
                const subVerticalNames = fieldSelections.VERTICAL.flatMap(
                    (item) => VerticalToSubVerticalMap[item.label],
                ).filter((name) => !!name);
                const subVerticalCategories = FilterToCategoriesMapping[
                    MarketingNarrativesFilterField.SUBVERTICAL
                ].filter((category) =>
                    subVerticalNames.includes(category.displayName),
                );
                items = getItemsFromCategories(subVerticalCategories);
            }
            setFilterOptions(
                filterKey,
                sortBy(items, (item) => item.label),
            );
        }
        if (fieldSelections.SUBVERTICAL) {
            let items: Item[] = [];
            const filterKey = MarketingNarrativesFilterField.VERTICAL;
            if (fieldSelections.SUBVERTICAL.length === 0) {
                items = getItemsFromCategories(
                    FilterToCategoriesMapping[
                        MarketingNarrativesFilterField.VERTICAL
                    ],
                );
            } else {
                const verticalNames = Object.keys(
                    VerticalToSubVerticalMap,
                ).filter((verticalKey) => {
                    if (
                        fieldSelections.SUBVERTICAL.some((item) =>
                            VerticalToSubVerticalMap[verticalKey].includes(
                                item.label,
                            ),
                        )
                    ) {
                        return true;
                    }
                    return false;
                });
                const verticalCategories = FilterToCategoriesMapping[
                    MarketingNarrativesFilterField.VERTICAL
                ].filter((vertical) =>
                    verticalNames.includes(vertical.displayName),
                );
                items = getItemsFromCategories(verticalCategories);
            }
            setFilterOptions(
                filterKey,
                sortBy(items, (item) => item.label),
            );
        }
    }, []);

    return {
        applyMarketingNarrativesCascading,
        applyDefaultMarketingNarrativeFilters,
    };
};
